import { useEffect, useRef } from "react";
import useToastStore from "../../console/store/useToastStore";
import alertSound from "../../console/assets/sound/alarm.mp3";

/**
 * 지점 신규 발주 음성 알림
 *
 * 본점 useGetAlertList 의 음성 로직을 그대로 가져왔다.
 * 다만 알림 출처가 다르다. 본점은 /v1/console/alert(전사 공용 알림)을 폴링하는데,
 * 그 API는 계정 구분이 없어 지점에 열면 고려원예 전체 주문 알림이 새어 나간다.
 *
 * 기준은 발주 시각(branch_date)이다. 미확인 건수로 잡으면 본점이 이미 접수완료로
 * 바꾼 주문을 지점으로 넘길 때 알림이 뜨지 않는다. orderBranchModify 가
 * order_status 를 건드리지 않기 때문이다(order.js:3327).
 *
 * 폴링 때마다 반복해 말하지 않도록, 마지막 발주 시각이 이전보다 앞설 때만 알린다.
 * 화면을 처음 열었을 때는 기준값만 잡고 넘어간다.
 */
const nativeKoreanTens: Record<number, string> = {
    10: "열",
    20: "스물",
    30: "서른",
    40: "마흔",
    50: "쉰",
    60: "예순",
    70: "일흔",
    80: "여든",
    90: "아흔",
};

const nativeKoreanOnes: Record<number, string> = {
    1: "한",
    2: "두",
    3: "세",
    4: "네",
    5: "다섯",
    6: "여섯",
    7: "일곱",
    8: "여덟",
    9: "아홉",
};

const formatOrderCount = (n: number): string => {
    if (n <= 0) return "0껀";
    if (n < 10) return `${nativeKoreanOnes[n]} 껀`;

    if (n < 100) {
        const tens = Math.floor(n / 10) * 10;
        const ones = n % 10;

        let tensWord = nativeKoreanTens[tens] || "";
        const onesWord = ones ? nativeKoreanOnes[ones] : "";

        if (tens === 20 && ones === 0) tensWord = "스무";

        return `${tensWord}${onesWord} 껀`;
    }

    return `${n} 껀`;
};

const getKoreanVoice = async (): Promise<SpeechSynthesisVoice | null> => {
    if (!("speechSynthesis" in window)) return null;

    const selectKoreanVoice = (voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null => {
        const koreans = voices.filter(v => v.lang.startsWith("ko"));
        if (koreans.length === 0) return null;
        return koreans.find(v => v.name.includes("Google")) || koreans[0];
    };

    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) return selectKoreanVoice(voices);

    return new Promise(resolve => {
        const handleVoicesChanged = () => {
            const loaded = window.speechSynthesis.getVoices();
            window.speechSynthesis.removeEventListener("voiceschanged", handleVoicesChanged);
            resolve(selectKoreanVoice(loaded));
        };
        window.speechSynthesis.addEventListener("voiceschanged", handleVoicesChanged);
    });
};

const playAlertSound = () => {
    const audio = new Audio(alertSound);
    audio.play().catch(() => console.warn("🔇 사용자 상호작용 전이라 소리 재생 불가"));
};

const speakAlert = async (text: string): Promise<boolean> => {
    if (!("speechSynthesis" in window)) return false;

    try {
        const voice = await getKoreanVoice();
        if (!voice) return false;

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = "ko-KR";
        utterance.rate = 1;
        utterance.pitch = 1;
        utterance.volume = 1.5;
        utterance.voice = voice;

        return await new Promise<boolean>(resolve => {
            utterance.onstart = () => resolve(true);
            utterance.onerror = () => resolve(false);

            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(utterance);
        });
    } catch {
        return false;
    }
};

export const useBranchOrderAlert = (latestBranchDate: string | null | undefined, newCount: number | undefined) => {
    const { addToast } = useToastStore();
    const previousLatest = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        if (latestBranchDate === undefined) return;

        const previous = previousLatest.current;
        previousLatest.current = latestBranchDate;

        // 화면을 처음 연 시점은 기준만 잡는다
        if (previous === undefined) return;

        // 마지막 발주 시각이 앞선 경우 = 새 발주가 들어온 경우
        const hasNewOrder = !!latestBranchDate && (!previous || latestBranchDate > previous);
        if (!hasNewOrder) return;

        const count = newCount && newCount > 0 ? newCount : 1;

        addToast({ type: "notice", message: `신규 발주가 ${count}건 있습니다.` });

        speakAlert(`신규 발주가 ${formatOrderCount(count)} 있습니다.`).then(success => {
            if (!success) playAlertSound();
        });
    }, [latestBranchDate, newCount, addToast]);
};
